import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Table, TableBody, TableHeader, TableHeaderColumn, TableRow } from 'material-ui/Table';
import Pagination from 'components/Libs/Pagination';
import Dialog from 'material-ui/Dialog';

import { getVessels } from 'actions/vessels/vessels';
import api from 'services';
import styles from 'styles';
import VesselRow from './Row';
import VesselImport from './VesselImport';

import VesselsListFilters from './Filters';
import { CenterBox } from './utils';
import useVesselFilters from './useVesselFilters';

function VesselListTable(props) {
    const { vessels, getVessels } = props;

    const rowsPerPage = [25, 50, 100];

    const [rows, setRows] = useState([]);
    const [numberOfRows, setNumberOfRows] = useState(25);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(undefined);
    const [vesselImportModalOpen, setVesselImportModalOpen] = useState(false);
    const [filterCargoExclusions, setFilterCargoExclusions] = useState(null);
    const [filterTradingRestrictions, setFilterTradingRestrictions] = useState(null);
    const [search] = useState();

    const {
        filters,
        errors,
        syncFromRedux,
        handleFilters,
        handleNameSuggested,
        handleConfirmed,
        handleSanctioned,
        handleMarkedToDelete,
    } = useVesselFilters({ getVessels, numberOfRows, page });

    const searchRef = useRef(null);
    const importDialogRef = useRef(null);

    useEffect(() => {
        if (!vessels) return;

        setTotal(vessels.total);
        setRows(vessels.data);
        setPage(vessels.page);
        
        setNumberOfRows(vessels.limit);
        setFilterCargoExclusions(vessels.filterCargoExclusions);
        setFilterTradingRestrictions(vessels.filterTradingRestrictions);
        syncFromRedux(vessels);
    }, [vessels]);

    const clearSearch = useCallback((_e, value) => {
        if ((value || '').length === 0) {
            getVessels({
                page,
                limit: numberOfRows,
            });
        }
    }, [getVessels, page, numberOfRows]);

    const handleSearch = useCallback(async () => {
        const value = ((searchRef.current && searchRef.current.input && searchRef.current.input.value) || '').trim();
        if (searchRef.current && searchRef.current.input) {
            searchRef.current.input.value = value;
        }

        if (!value.length) {
            return;
        }

        await api.findVessel(value, { confirmed: !filters.notConfirmed, isNameSuggestion: filters.isNameSuggestion, suggestionSource: filters.suggestionSource })
            .then((response) => {
                if (response.status === 200) {
                    setRows(response.data.data);
                    setPage(1);
                    setNumberOfRows(25);
                }
            });
    }, [filters.notConfirmed, filters.isNameSuggestion, filters.suggestionSource, filters.isMarkedToDelete]);

    const updateRows = useCallback((state) => {
        getVessels({
            page: state.page,
            limit: state.numberOfRows,
            ...filters,
        });
    }, [
        getVessels,
        filters,
    ]);

    const handleCloseImportVessels = useCallback(() => setVesselImportModalOpen(false), []);
    const handleUpdateImportDialog = useCallback(() => {
        if (importDialogRef.current && typeof importDialogRef.current.forceUpdate === 'function') {
            importDialogRef.current.forceUpdate();
        }
    }, []);

    const handleExclusions = useCallback((period, property) => {
        if (!property) return;
        const nextVal = period ? new Date(period).toISOString() : undefined;

        if (property === 'filterCargoExclusions') {
            setFilterCargoExclusions(nextVal);
        } else if (property === 'filterTradingRestrictions') {
            setFilterTradingRestrictions(nextVal);
        }

        getVessels({
            page,
            limit: numberOfRows,
            isNameSuggestion: filters.isNameSuggestion,
            suggestionSource: filters.suggestionSource,
            notConfirmed: filters.notConfirmed,
            sanctioned: filters.sanctioned,
            photosFilter: filters.photosFilter,
            isMarkedToDelete: filters.isMarkedToDelete,
            [property]: nextVal,
        });
    }, [getVessels, page, numberOfRows, filters]);

    const pagination = (
        <div
            className="center-xs middle-xs"
            style={{ display: 'flex' }}
        >
            <Pagination
                total={total}
                rowsPerPage={rowsPerPage}
                page={page}
                numberOfRows={numberOfRows}
                updateRows={(s) => {
                    setPage(s.page);
                    setNumberOfRows(s.numberOfRows);
                    updateRows(s);
                }}
            />
        </div>
    );

    return (
        <div>
            <Dialog
                ref={importDialogRef}
                title="Import/Update vessels from LOTUS"
                modal={false}
                open={vesselImportModalOpen}
                onRequestClose={handleCloseImportVessels}
                autoScrollBodyContent
                style={{ height: '60vh', width: '90vw' }}
            >
                <VesselImport onUpdate={handleUpdateImportDialog} />
            </Dialog>

            <VesselsListFilters
                searchRef={searchRef}
                defaultSearchValue={search}
                clearSearch={clearSearch}
                handleSearch={handleSearch}
                handleNameSuggested={handleNameSuggested}
                handleConfirmed={handleConfirmed}
                handleMarkedToDelete={handleMarkedToDelete}
                handleFilters={handleFilters}
                filterCargoExclusions={filterCargoExclusions}
                filterTradingRestrictions={filterTradingRestrictions}
                handleSanctioned={handleSanctioned}
                handleExclusions={handleExclusions}
                filters={filters}
                errors={errors}
                rightPart={
                    <React.Fragment>
                        <div className="center-xs middle-xs" style={{ display: 'flex' }}>
                            <span>VESSELS: {vessels.total}</span>
                        </div>
                        <CenterBox>{pagination}</CenterBox>
                    </React.Fragment>
                }
            />

            <div className="table-body">
                <Table selectable={false}>
                    <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                        <TableRow>
                            <TableHeaderColumn style={{ ...styles.tableRowColumnSmallCSS, textAlign: 'center' }}>Imo Number</TableHeaderColumn>
                            <TableHeaderColumn style={{ ...styles.tableRowColumnSmallCSS, textAlign: 'center' }}>Name</TableHeaderColumn>
                            <TableHeaderColumn style={{ ...styles.tableRowColumnSmallCSS, textAlign: 'center' }}>Year</TableHeaderColumn>
                            <TableHeaderColumn style={{ ...styles.tableRowColumnSmallCSS, textAlign: 'center' }}>Type By Purpose</TableHeaderColumn>
                            <TableHeaderColumn style={{ ...styles.tableHeaderColumnCSS, textAlign: 'center' }}>Class</TableHeaderColumn>
                            <TableHeaderColumn style={{ ...styles.tableHeaderColumnCSS, textAlign: 'center' }}>Gears</TableHeaderColumn>
                            <TableHeaderColumn style={{ ...styles.tableRowColumnSmallCSS, textAlign: 'center' }}>Capacity (G/B)</TableHeaderColumn>
                            <TableHeaderColumn style={{ ...styles.tableRowColumnSmallCSS, textAlign: 'center' }}>DWT</TableHeaderColumn>
                            <TableHeaderColumn style={{ ...styles.tableRowColumnModified, textAlign: 'center' }}>Modified</TableHeaderColumn>
                            <TableHeaderColumn style={{ ...styles.tableRowColumnModified, textAlign: 'center' }}>Sanction (RT)</TableHeaderColumn>
                            <TableHeaderColumn style={{ ...styles.tableRowColumnModified, textAlign: 'center' }}>Data Fill Ratio</TableHeaderColumn>
                            <TableHeaderColumn style={{ ...styles.tableRowColumnSmallCSS, textAlign: 'center' }}>Actions</TableHeaderColumn>
                        </TableRow>
                    </TableHeader>

                    <TableBody displayRowCheckbox={false}>
                        {rows.map(item => (
                            <VesselRow key={item._id} item={item} />
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

export default connect(
    ({ vessels, user }) => ({
        vessels,
        userId: user._id,
    }),
    dispatch => bindActionCreators(
        {
            getVessels,
        },
        dispatch,
    ),
)(VesselListTable);



// WEBPACK FOOTER //
// ./app/components/Vessels/List/Table.jsx